import { useEffect, useRef, useState } from "react";
import type { PhoneConfig, VoiceAttackerSpec } from "../scenario/types";
import { outcomeFromScoringEvent, useSim } from "../state/store";
import { audio } from "../lib/audio";
import { listenForCode, type CodeListener } from "../lib/listenForCode";
import { Keypad } from "./Keypad";
import { SkipButton } from "../ui/SkipButton";
import { tx } from "../lib/tx";

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

type Phase = "ringing" | "connecting" | "talking" | "awaiting" | "closing" | "ended";

/**
 * The simulated vishing call — a CONTROLLED, scripted caller ("Dave") plus a
 * real learner input choice: SPEAK the code (browser speech-to-text) AND/OR type
 * it on the keypad. Both converge on onCode. Dave speaks each line fully (TTS),
 * waits for the code, re-asks if idle, and never auto-continues or goes
 * off-script. Reliable on every device (no live-model cut-offs / drift).
 */
export function PhoneCall({
  config,
  attacker,
  beatId,
  onEnded,
  onPhaseChange,
}: {
  config: PhoneConfig;
  attacker: VoiceAttackerSpec;
  beatId: string;
  onEnded: (key?: string) => void;
  onPhaseChange?: (phase: string) => void;
}) {
  const push = useSim((s) => s.pushTranscript);
  const update = useSim((s) => s.updateTranscript);
  const record = useSim((s) => s.recordOutcome);
  const clearTranscript = useSim((s) => s.clearTranscript);
  const [phase, setPhase] = useState<Phase>("ringing");
  const [padReady, setPadReady] = useState(false);
  useEffect(() => {
    const reported =
      padReady && (phase === "talking" || phase === "awaiting") ? "code-ready" : phase;
    onPhaseChange?.(reported);
  }, [phase, padReady, onPhaseChange]);
  const [seconds, setSeconds] = useState(0);
  const [listening, setListening] = useState(false);

  const mounted = useRef(true);
  const submittedRef = useRef(false);
  const padReadyRef = useRef(false);
  const nudgedRef = useRef(false);
  const idleRef = useRef<number | null>(null);
  const hangUpRef = useRef<() => void>(() => {});
  const listenerRef = useRef<CodeListener | null>(null);
  const livePushed = useRef(false);
  // See VideoCall.tsx's skipRef doc — same fast-forward pattern, single
  // target here (the "awaiting" decision: give the code, or hang up).
  const skipRef = useRef(false);

  const lang = attacker.persona.language === "en" || !attacker.persona.language ? "en-US" : attacker.persona.language;
  const voice = attacker.persona.voiceId || "onyx";
  const tone =
    "Speak as a calm, professional bank fraud-department phone agent: reassuring and friendly with a subtle, controlled sense of urgency. Natural conversational pacing, like a real person on a call.";
const LINE_CLARIFY =
  "Is everything clear so far? Whenever you're ready, just read me the code from your authenticator app — or type it in on the keypad for me.";
const LINE_NUDGE =
  "No rush at all, I'll stay on the line. Whenever you're ready, say the code or tap it in for me.";

  useEffect(() => {
    // `transcript` is shared across every call-style device in the course —
    // clear it so an earlier call's lines never bleed into this one.
    clearTranscript();
    audio.startRing();
    // Warm the natural-voice lines so they play instantly after pickup.
    const opts = { lang, voice, instructions: tone };
    void audio.prefetchSpeech(attacker.opening, opts);
    void audio.prefetchSpeech(LINE_CLARIFY, opts);
    void audio.prefetchSpeech(LINE_NUDGE, opts);
    void audio.prefetchSpeech(
      "Perfect — that's verified on our side. Thank you, Alice. You're all set. Goodbye.",
      opts
    );
    return () => {
      mounted.current = false;
      audio.stopRing();
      audio.cancelSpeech();
      listenerRef.current?.stop();
      if (idleRef.current) window.clearTimeout(idleRef.current);
    };
  }, []);

  // Call timer (runs once connected).
  useEffect(() => {
    if (phase === "ringing" || phase === "ended") return;
    const t = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));
  const say = async (id: string, text: string) => {
    if (!mounted.current) return;
    await audio.speak(text, {
      lang,
      voice,
      instructions: tone,
      onStart: () => {
        if (mounted.current) push({ id, role: "attacker", text, final: true });
      },
    });
    if (audio.isEnabled()) return;
    await wait(Math.min(4500, 1200 + text.length * 35));
  };

  const startListening = () => {
    if (listenerRef.current || !config.mfaCode) return;
    const l = listenForCode({
      lang,
      minLen: config.mfaCode.length,
      onDigits: (d) => onCode(d, "voice"),
      onText: (t) => {
        if (!t || submittedRef.current) return;
        if (!livePushed.current) {
          livePushed.current = true;
          push({ id: "usr_live", role: "learner", text: t, final: false });
        } else {
          update("usr_live", t, false);
        }
      },
    });
    listenerRef.current = l;
    setListening(!!l);
  };

  const startIdle = () => {
    if (idleRef.current) window.clearTimeout(idleRef.current);
    idleRef.current = window.setTimeout(async () => {
      if (!mounted.current || submittedRef.current) return;
      if (padReadyRef.current) {
        startIdle();
        return;
      }
      if (!nudgedRef.current) {
        nudgedRef.current = true;
        await say("att_nudge", LINE_NUDGE);
        if (mounted.current && !submittedRef.current) startIdle();
        return;
      }
      hangUpRef.current();
    }, padReadyRef.current ? 1000 : 15000);
  };

  const answer = async () => {
    skipRef.current = false;
    audio.stopRing();
    setPhase("connecting");
    record({ type: "call_answered", beatId });
    await wait(700);
    if (!mounted.current || skipRef.current) return;
    setPhase("talking");
    await say("att_open", attacker.opening);
    if (!mounted.current || submittedRef.current || skipRef.current) return;
    await wait(300);
    await say("att_clarify", LINE_CLARIFY);
    if (!mounted.current || submittedRef.current || skipRef.current) return;
    setPhase("awaiting");
    startIdle();
    startListening();
  };

  /** Fast-forwards past passive ringing/connecting/talking narration straight
   *  to the code-or-hang-up decision. */
  const skipAhead = () => {
    if (submittedRef.current) return;
    skipRef.current = true;
    audio.stopRing();
    audio.cancelSpeech();
    if (phase === "ringing") record({ type: "call_answered", beatId });
    setPhase("awaiting");
    startIdle();
    startListening();
  };

  const onCode = async (entered: string, _via?: "voice" | "keypad") => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    if (idleRef.current) window.clearTimeout(idleRef.current);
    listenerRef.current?.stop();
    listenerRef.current = null;
    setListening(false);
    audio.cancelSpeech();
    const ev = attacker.scoringEvents.find((s) => s.id === "read_otp") ?? attacker.scoringEvents[0];
    if (ev) record(outcomeFromScoringEvent(beatId, ev, `gave code ${entered}`));
    if (livePushed.current) update("usr_live", `My code is ${entered}.`, true);
    else push({ id: "usr_code", role: "learner", text: `My code is ${entered}.`, final: true });
    setPhase("closing");
    await wait(500);
    await say("att_thanks", "Perfect — that's verified on our side. Thank you, Alice. You're all set. Goodbye.");
    await wait(500);
    end();
  };

  /** Hang-up before any code was given: a refusal. Ends with outcome key
   *  "safe" (scenarios without an `on:"safe"` edge fall back to the default). */
  const hangUp = () => {
    if (!submittedRef.current) {
      const ev = attacker.scoringEvents.find((s) => s.id === "refused_request");
      if (ev) record(outcomeFromScoringEvent(beatId, ev, "hung up without giving the code"));
    }
    end(submittedRef.current ? undefined : "safe");
  };
  hangUpRef.current = hangUp;

  const end = (key?: string) => {
    audio.stopRing();
    audio.cancelSpeech();
    listenerRef.current?.stop();
    listenerRef.current = null;
    if (idleRef.current) window.clearTimeout(idleRef.current);
    setPhase("ended");
    window.setTimeout(() => onEnded(key), 500);
  };

  const ringing = phase === "ringing";
  const connecting = phase === "connecting";
  const canSkipAhead = ringing || connecting || phase === "talking";
  const connected = phase === "talking" || phase === "awaiting" || phase === "closing";
  const showKeypad = (phase === "talking" || phase === "awaiting") && !!config.mfaCode && !submittedRef.current;

  return (
    <>
      <div className="device-layer">
        <div
          className={`phone ${connected ? "in-call" : ""}`}
          style={{
            backgroundImage: config.wallpaperUrl
              ? `url(${config.wallpaperUrl})`
              : "linear-gradient(160deg,#10231d,#06100d)",
          }}
        >
          {canSkipAhead && (
            <SkipButton className="cf-skip-btn--compact phone-skip" label={tx("Skip ahead")} onClick={skipAhead} />
          )}
          <div className="phone-stack">
            <div className="ring-top">
              <div className="caller">{config.callerName}</div>
              <div className="caller-sub">{config.callerSubtitle ?? ""}</div>
              {ringing && <div className="ringing">{tx("incoming call…")}</div>}
              {connecting && <div className="ringing">{tx("connecting…")}</div>}
            </div>

            {connected && <div className="timer">{fmt(seconds)}</div>}

            {showKeypad && (
              <Keypad
                targetCode={config.mfaCode!}
                prompt={config.mfaPrompt}
                onSubmit={(c) => onCode(c, "keypad")}
                onReadyChange={(ready) => {
                  const was = padReadyRef.current;
                  padReadyRef.current = ready;
                  setPadReady(ready);
                  if (ready !== was && idleRef.current) startIdle();
                }}
              />
            )}

            {phase === "awaiting" && (
              <div className="mic-hint">
                {listening ? (
                  <>
                    <span className="mic-dot" /> {tx("Listening — say the code, or tap it in")}
                  </>
                ) : (
                  <>{tx("Tap the code in on the keypad")}</>
                )}
              </div>
            )}

            {phase === "closing" && <div className="call-coach">{tx("Verifying…")}</div>}

            <div className="actions">
              {ringing && (
                <button className="call-btn answer" onClick={() => answer()} aria-label={tx("Answer")}>
                  {"\u{1F4DE}"}
                </button>
              )}
              {(connected || connecting) && (
                <button className="call-btn hangup" onClick={hangUp} aria-label={tx("Hang up")}>
                  {"\u{1F4F4}"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
